import { useState, useRef, useEffect } from 'react';
import { Textarea, Button } from '@mantine/core';
import { IconNotes } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import CardModal from './card-modal';

interface Props {
    value: string | null | undefined;
    onChange: (value: string) => void | Promise<void>;
    placeholder?: string;
    label?: string;
    minRows?: number;
    maxRows?: number;
    disabled?: boolean;
    loading?: boolean;
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    customTrigger?: (onClick: () => void) => React.ReactNode;
    opened?: boolean;
    onClose?: () => void;
}

export default function EditTextModal({
    value,
    onChange,
    placeholder,
    label,
    minRows = 2,
    maxRows = 6,
    disabled = false,
    loading = false,
    size = 'sm',
    customTrigger,
    opened: controlledOpened,
    onClose: controlledOnClose,
}: Props) {
    const { t } = useTranslation();
    const [internalOpened, setInternalOpened] = useState(false);
    const opened = controlledOpened !== undefined ? controlledOpened : internalOpened;
    const setOpened = (val: boolean) => {
        if (controlledOnClose && !val) {
            controlledOnClose();
        } else {
            setInternalOpened(val);
        }
    };
    const [draft, setDraft] = useState(value || '');
    const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        setDraft(value || '');
    }, [value]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
        };
    }, []);

    const handleSave = async () => {
        // Clear any existing timeout
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }

        await onChange(draft);
        setOpened(false);
    };

    const handleCancel = () => {
        setDraft(value || '');
        setOpened(false);
    };

    return (
        <>
            {customTrigger ? (
                customTrigger(() => !disabled && setOpened(true))
            ) : controlledOpened !== undefined ? null : (
                <div
                    className={`
                        flex items-center gap-2
                        cursor-pointer 
                        bg-red-100
                        p-2 rounded-md border border-red-200
                        ${!value ? 'text-gray-400' : 'text-gray-800'}
                        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
                        w-fit
                    `}
                    onClick={() => !disabled && setOpened(true)}
                >
                    <IconNotes size={16} stroke={1} color="gray" />
                    <div className={`text-${size}`}>
                        {value || placeholder || t('notes')}
                    </div>
                </div>
            )}

            <CardModal
                withCloseButton={false}
                opened={opened}
                onClose={handleCancel}
                header={<div className='font-bold'>{label || t('notes')}</div>}
                size="md"
            >
                <div className="space-y-3">
                    <Textarea
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        autosize
                        minRows={minRows}
                        maxRows={maxRows}
                        disabled={loading}
                    />
                    <div className="flex gap-2 justify-end">
                        <Button
                            color="black"
                            size="xs"
                            variant="subtle"
                            onClick={handleCancel}
                            disabled={loading}
                        >
                            {t('cancel')}
                        </Button>
                        <Button
                            color="black"
                            size="xs"
                            radius="xl"
                            onClick={handleSave}
                            loading={loading}
                        >
                            {t('save')}
                        </Button>
                    </div>
                </div>
            </CardModal>
        </>
    );
}